<script>
	// NPM Modules
	import { navigate } from 'svelte-navaid';
	// Stores
	import SportStore from '@/Stores/Sport/SportStore.js';
	import MarketGroupStore from '@/Stores/Sport/MarketGroupStore.js';
	import ParentMarketStore from '@/Stores/Sport/ParentMarketStore.js';
	import ACLStore from '@/Stores/ACLStore.js';
	// Components
	import Loading from '@/Components/Loading.svelte';
	import Icon from '@/Components/Icon.svelte';
	import LoadingButton from '@/Components/LoadingButton.svelte';
	import SelectInput from '@/Components/SelectInput.svelte';
	import PillMultiSelect from '@/Components/PillMultiSelect.svelte';
	import CheckBox from '@/Components/CheckBox.svelte';
	// Services
	import { notify } from '@/Services/Notify.js';
	import { removeUrlParameter, setUrlParameter } from '@/Services/Helpers';

	export let id;
	export let sportId;

	let sending = false;
	let selectedSport = sportId;
	let showParentMarketsUnderTypes = false;
	let showSelectedMarketsUnderTypes = true;
	let loading =
		SportStore.getSports() && id === 'create'
			? MarketGroupStore.newMarketGroup()
			: MarketGroupStore.getMarketGroup(id).then(() => (selectedSport = $MarketGroupStore.sport.id));

	$: selectedSport && selectedSportChanged();
	$: selectedMarkets = $MarketGroupStore && $MarketGroupStore.grouped_parent_markets;
	$: selectedMarketTypes = selectedMarkets && selectedMarkets.sortBy('order').pluck('market_type.id').unique();
	$: parentMarketTypes = $ParentMarketStore && $ParentMarketStore.sortBy('order').pluck('market_type.id').unique();

	function onClickSave() {
		if (!$MarketGroupStore.name || !selectedSport) {
			notify({
				type: 'danger',
				title: 'Error!',
				message: 'Market Group must have a name and sport',
				timeout: 3
			});
			return;
		}

		sending = true;
		MarketGroupStore.saveMarketGroup()
			.then(() => {
				notify({
					type: 'success',
					title: $MarketGroupStore.name,
					message: 'Market Group Saved',
					timeout: 3
				});
				if (id === 'create') navigate(`/sport/markets/groups${selectedSport ? '?sport=' + selectedSport : ''}`);
			})
			.catch((e) => {
				notify({
					type: 'danger',
					title: 'Error!',
					message: (e.error && e.error[0]) || 'Failed to save Market Group',
					timeout: 5
				});
			})
			.finally(() => (sending = false));
	}

	function selectedSportChanged() {
		MarketGroupStore.updateSelectedSport(selectedSport);
		ParentMarketStore.getParentMarketsForSport(selectedSport);
	}

	function moveMarketUp(market) {
		let gpm = $MarketGroupStore.grouped_parent_markets;
		let neighbour = gpm
			.where('market_type.id', market.market_type.id)
			.sortBy('order')
			.where('order', '<', market.order)
			.last();
		if (neighbour) {
			MarketGroupStore.swapMarketOrder(market, neighbour);
		}
	}

	function moveMarketDown(market) {
		let gpm = $MarketGroupStore.grouped_parent_markets;
		let neighbour = gpm
			.where('market_type.id', market.market_type.id)
			.sortBy('order')
			.where('order', '>', market.order)
			.first();
		if (neighbour) {
			MarketGroupStore.swapMarketOrder(market, neighbour);
		}
	}

	function moveMarketTypeUp(marketTypeId) {
		let gpm = $MarketGroupStore.grouped_parent_markets;
		let market = gpm.where('market_type.id', marketTypeId).sortBy('order').first();
		let neighbour = gpm.sortBy('order').where('order', '<', market.order).last();
		if (neighbour) {
			MarketGroupStore.swapMarketTypeOrder(marketTypeId, neighbour.market_type.id);
		}
	}

	function moveMarketTypeDown(marketTypeId) {
		let gpm = $MarketGroupStore.grouped_parent_markets;
		let market = gpm.where('market_type.id', marketTypeId).sortBy('order').last();
		let neighbour = gpm.sortBy('order').where('order', '>', market.order).first();
		if (neighbour) {
			MarketGroupStore.swapMarketTypeOrder(marketTypeId, neighbour.market_type.id);
		}
	}

	function changeSport(sport_id) {
		if (typeof parseInt(sport_id) !== 'undefined' && sport_id !== false) {
			setUrlParameter('sport', sport_id);
			sportId = sport_id;
		} else {
			removeUrlParameter('sport');
			sportId = false;
		}
	}
</script>

<div>
	{#await loading}
		<Loading />
	{:then}
		{#if $SportStore && $MarketGroupStore}
			<div>
				<!-- Page Header -->
				<div class="flex flex-col items-center w-full p-4 mb-4 text-sm bg-white border-b md:py-2">
					<div class="flex items-center w-full">
						<a
							href={`/sport/markets/groups${selectedSport ? '?sport=' + selectedSport : ''}`}
							title="Market Groups"
							class="flex cursor-pointer hover:underline text-grey items-center"
						>
							<Icon name="chevron-left" classes="w-2 mr-1" />
							<div class="mr-6 text-xl font-bold">Market Groups</div>
						</a>
						<h1 class="flex-1 mr-2 text-xl font-bold">
							<input
								type="text"
								value={$MarketGroupStore.name}
								placeholder="Market Group Name"
								on:change={(e) => MarketGroupStore.updateName(e.target.value)}
								class="w-1/2 font-semibold border-b focus:outline-none"
							/>
						</h1>
						<div class="flex items-center space-x-2">
							<div class="flex items-center">
								{#if $ACLStore && ACLStore.granted('market_groups.update')}
									<LoadingButton className="btn btn-black" on:click={onClickSave} loading={sending}>Save</LoadingButton>
								{/if}
							</div>
						</div>
					</div>
				</div>
				<!-- /Page Header -->
				<div class="p-2 mx-4 bg-gray-100 rounded">
					{#if id === 'create'}
						<SelectInput
							className="py-1 w-1/3"
							name="sport"
							required={true}
							bind:value={selectedSport}
							on:change={(event) => changeSport(event.target.value)}
						>
							<option value={false}>Select Sport...</option>
							{#each $SportStore.sortBy('name').all() as sport (sport.id)}
								{#if parseInt(sport.id) === parseInt(sportId)}
									<option value={sport.id} selected>{sport.name}</option>
								{:else}
									<option value={sport.id}>{sport.name}</option>
								{/if}
							{/each}
						</SelectInput>
					{:else}
						<div class="font-semibold">
							Sport: {$SportStore.where('id', selectedSport).pluck('name').first()}
						</div>
					{/if}
					<div class="w-full">
						<!-- START PARENT MARKETS -->
						<div class="my-4">
							<div class="flex w-full space-x-2">
								<!-- MARKETS SELECTED -->
								<div class="w-2/3">
									<div class="flex items-center w-full mb-2 space-x-4">
										<div class="font-semibold">
											Markets in this group ({selectedMarkets.count()})
										</div>
										<CheckBox bind:checked={showSelectedMarketsUnderTypes} size="4" text="Show Markets" />
										<button
											on:click={() => MarketGroupStore.resetGroupedParentMarkets()}
											class="py-1 -mb-1 text-xs font-normal btn btn-primary-action"
										>
											Clear
										</button>
									</div>
									<div class="w-full h-screen p-1 overflow-y-auto bg-gray-200 border">
										{#if selectedMarkets.count() === 0}
											<div class="py-4 mx-2 text-center">Add some markets to this group...</div>
										{/if}
										{#each selectedMarketTypes.all() as marketType}
											<div class="flex items-center w-full space-x-2">
												<div class="flex flex-col -mr-1 text-white bg-green-500 rounded-full">
													<button
														on:click={() => moveMarketTypeUp(marketType)}
														class="px-1 rounded-t-full focus:outline-none hover:bg-green-600"
													>
														<Icon name="chevron-up" classes="w-2 h-4" />
													</button>
													<button
														on:click={() => moveMarketTypeDown(marketType)}
														class="px-1 rounded-b-full focus:outline-none hover:bg-green-600"
													>
														<Icon name="chevron-down" classes="w-2 h-4" />
													</button>
												</div>
												<PillMultiSelect
													action="remove"
													on:remove={() => MarketGroupStore.removeParentMarketType(marketType)}
													bgColour={selectedMarkets
														.where('market_type.id', marketType)
														.pluck('market_type.is_manual')
														.first()
														? 'bg-orange-500'
														: 'bg-green-500'}
													label={selectedMarkets.where('market_type.id', marketType).pluck('market_type.name').first() +
														` (${selectedMarkets.where('market_type.id', marketType).count()})`}
												/>
											</div>
											{#if showSelectedMarketsUnderTypes}
												<div class="ml-6">
													{#each selectedMarkets.where('market_type.id', marketType).sortBy('order').all() as market}
														<div class="flex items-center space-x-2">
															{#if selectedMarkets.where('market_type.id', marketType).count() > 1}
																<div class="flex flex-col -mr-1 text-white bg-blue-500 rounded-full">
																	<button
																		on:click={() => moveMarketUp(market)}
																		class="px-1 rounded-t-full focus:outline-none hover:bg-blue-600"
																	>
																		<Icon name="chevron-up" classes="w-2 h-4" />
																	</button>
																	<button
																		on:click={() => moveMarketDown(market)}
																		class="px-1 rounded-b-full focus:outline-none hover:bg-blue-600"
																	>
																		<Icon name="chevron-down" classes="w-2 h-4" />
																	</button>
																</div>
															{/if}
															<PillMultiSelect
																action="remove"
																on:remove={() => MarketGroupStore.removeParentMarketId(market.parent_market_id)}
																bgColour={market.market_type.is_manual ? 'bg-purple-500' : 'bg-blue-500'}
																label={market.name}
															/>
														</div>
													{/each}
												</div>
											{/if}
										{/each}
									</div>
								</div>
								<!-- AVAILABLE MARKETS TO SELECT FROM -->
								<div class="w-1/3">
									<div class="flex items-center justify-between w-full mb-2">
										<div class="font-semibold">
											Available Markets
											{#if selectedSport}
												({`${$ParentMarketStore.count() - selectedMarkets.count()}/${$ParentMarketStore.count()}`})
											{/if}
										</div>
										<CheckBox bind:checked={showParentMarketsUnderTypes} size="4" text="Show Markets" />
									</div>
									<div class="w-full h-screen p-2 overflow-y-auto bg-gray-200 border">
										{#if selectedSport}
											{#each parentMarketTypes.all() as parentMarketType}
												<!-- ONLY SHOW THE MARKET TYPE WITH MARKETS IF ANY AVAILABLE TO SELECT -->
												{#if selectedMarkets.where('market_type.id', parentMarketType).count() < $ParentMarketStore
														.where('market_type.id', parentMarketType)
														.count()}
													<div class="w-full">
														<PillMultiSelect
															on:add={() => MarketGroupStore.addParentMarketType(parentMarketType)}
															bgColour={$ParentMarketStore
																.where('market_type.id', parentMarketType)
																.pluck('market_type.grouped')
																.first()
																? $ParentMarketStore
																		.where('market_type.id', parentMarketType)
																		.pluck('market_type.is_manual')
																		.first()
																	? 'bg-orange-700'
																	: 'bg-green-700'
																: $ParentMarketStore
																			.where('market_type.id', parentMarketType)
																			.pluck('market_type.is_manual')
																			.first()
																	? 'bg-orange-500'
																	: 'bg-green-500'}
															label={$ParentMarketStore
																.where('market_type.id', parentMarketType)
																.pluck('market_type.name')
																.first() +
																` (${
																	$ParentMarketStore.where('market_type.id', parentMarketType).count() -
																	selectedMarkets.where('market_type.id', parentMarketType).count()
																})`}
														/>
													</div>
													{#if showParentMarketsUnderTypes}
														<div class="ml-6">
															{#each $ParentMarketStore
																.whereNotIn('id', selectedMarkets.pluck('parent_market_id').toArray())
																.where('market_type.id', parentMarketType)
																.sortBy('order')
																.all() as market}
																<PillMultiSelect
																	on:add={() => MarketGroupStore.addParentMarket(market)}
																	bgColour={market.market_type.is_manual ? 'bg-purple-500' : 'bg-blue-500'}
																	label={market.name}
																/>
															{/each}
														</div>
													{/if}
												{/if}
											{/each}
										{/if}
									</div>
								</div>
							</div>
						</div>
						<!-- END PARENT MARKETS -->
					</div>
				</div>
			</div>
		{/if}
	{/await}
</div>
